import { useEffect, useState } from 'react';
import Cropper from 'react-cropper';
import CustomTooltip from '../../common/Tooltip';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';
import 'cropperjs/dist/cropper.css';

const DEFAULT_OVAL_ASPECT_RATIO = 0.75;

const PhotoCropper = ({ photo, cropperRef, onCrop, aspectRatioOverride, cropShapeOverride, initialCrop, onReady }) => {
  const isOverride = aspectRatioOverride !== undefined;
  const { selectedPhoto, form, lineItem, isEditInitializing, productType } = useProductSelectionStore();
  const matOpeningShape = form?.meta?.matOpeningShape;
  const savedMatOpeningShape = lineItem?.attributes?.frameMat?.openingShape;
  const paperTypeText = form?.inputs?.paperType?.selectedValueText || '';
  const printSizeValue = form?.inputs?.printSize?.selectedValue;
  const isPrintSizeSelected = !!printSizeValue && printSizeValue !== 'default';
  const canAdjustCrop = productType === 'multi-opening' || isPrintSizeSelected;
  // Circle/oval deckle prints are cropped to their own shape regardless of any mat opening.
  const isCircleDeckle = /circle\s*deckle/i.test(paperTypeText);
  // oval aspect varies by size, so wait for a print size to be picked
  const isOvalDeckle = /oval\s*deckle/i.test(paperTypeText) && isPrintSizeSelected;
  const defaultCropShape = isCircleDeckle ? 'circle' : isOvalDeckle ? 'oval' : matOpeningShape;
  const cropShape = cropShapeOverride !== undefined ? cropShapeOverride : defaultCropShape;
  const showShapeMask = cropShape === 'oval' || cropShape === 'circle';

  const [cropBoxRect, setCropBoxRect] = useState(null);
  const [showPrintSizeMessage, setShowPrintSizeMessage] = useState(false);

  useEffect(() => {
    setShowPrintSizeMessage(false);
  }, [printSizeValue, photo?.id, productType]);

  const handleCropInteraction = (event) => {
    if (canAdjustCrop) return;
    event.preventDefault();
    event.stopPropagation();
    setShowPrintSizeMessage(true);
  };

  // circle and oval shape radii
  const getShapeRadii = (box, shape) => {
    if (!box || !box.width || !box.height) return { rx: 50, ry: 50 };
    if (shape === 'circle') {
      const minSide = Math.min(box.width, box.height);
      return {
        rx: (50 * minSide) / box.width,
        ry: (50 * minSide) / box.height,
      };
    }
    return { rx: 50, ry: 50 };
  };

  // Apply aspect ratio when it changes
  useEffect(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    if (isOverride) {
      cropper.crop();
      cropper.setAspectRatio(aspectRatioOverride);
      return;
    }
    if (selectedPhoto === '' || !form) return;
    if (form.meta.aspectRatio !== undefined) {
      cropper.crop();
      cropper.setAspectRatio(form.meta.aspectRatio);
    } else if (cropShape === 'oval') {
      cropper.crop();
      cropper.setAspectRatio(DEFAULT_OVAL_ASPECT_RATIO);
    } else {
      cropper.clear();
    }
  }, [isOverride, aspectRatioOverride, form?.meta?.aspectRatio, selectedPhoto, cropperRef, cropShape]);

  // Restore crop data when editing a line item
  useEffect(() => {
    if (isOverride || !lineItem || !isEditInitializing) return undefined;
    if (savedMatOpeningShape && cropShape !== savedMatOpeningShape) return undefined;

    let retryCount = 0;
    const maxRetries = 1500;
    let cancelled = false;

    const waitForImage = () => {
      if (cancelled) return;
      const cropper = cropperRef.current?.cropper;
      if (!cropper) {
        retryCount++;
        if (retryCount < maxRetries) requestAnimationFrame(waitForImage);
        return;
      }

      const imageData = cropper.getImageData();
      if (imageData?.naturalWidth > 0 && imageData?.naturalHeight > 0) {
        const cropAttrs = lineItem?.attributes?.photographAttributes || {};

        const x = ((parseFloat(cropAttrs.cropX ?? cropAttrs.cropx) || 0) / 100) * imageData.naturalWidth;
        const y = ((parseFloat(cropAttrs.cropY ?? cropAttrs.cropy) || 0) / 100) * imageData.naturalHeight;
        const width = ((parseFloat(cropAttrs.cropW) || 0) / 100) * imageData.naturalWidth;
        const height = ((parseFloat(cropAttrs.cropH) || 0) / 100) * imageData.naturalHeight;

        cropper.crop();
        cropper.setData({ x, y, width, height });
      } else {
        retryCount++;
        if (retryCount < maxRetries) requestAnimationFrame(waitForImage);
        else console.warn('Cropper image never fully loaded.');
      }
    };

    waitForImage();
    return () => {
      cancelled = true;
    };
  }, [
    lineItem,
    isEditInitializing,
    savedMatOpeningShape,
    form?.meta?.aspectRatio,
    cropShape,
    cropperRef,
    isOverride,
  ]);

  const handleCrop = (event) => {
    if (onCrop) onCrop(event);
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    try {
      const box = cropper.getCropBoxData();
      if (box && box.width > 0 && box.height > 0) {
        setCropBoxRect(box);
      } else {
        setCropBoxRect(null);
      }
    } catch (e) {
      setCropBoxRect(null);
    }
  };

  if (!photo?.attributes) return null;

  const handleOnReady = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    const aspectRatio = isOverride
      ? aspectRatioOverride
      : form?.meta?.aspectRatio ?? (cropShape === 'oval' ? DEFAULT_OVAL_ASPECT_RATIO : undefined);

    if (aspectRatio !== undefined) {
      cropper.crop();
      cropper.setAspectRatio(aspectRatio);

      if (isOverride && initialCrop) {
        const imageData = cropper.getImageData();
        const originalWidth = imageData?.naturalWidth;
        const originalHeight = imageData?.naturalHeight;
        if (originalWidth && originalHeight) {
          cropper.setData({
            x: ((initialCrop.x || 0) / 100) * originalWidth,
            y: ((initialCrop.y || 0) / 100) * originalHeight,
            width: ((initialCrop.width || 0) / 100) * originalWidth,
            height: ((initialCrop.height || 0) / 100) * originalHeight,
          });
        }
      }
    }
    onReady?.();
  };

  return (
    <div
      className="picture-product-selection"
      style={{ position: 'relative' }}
      onPointerDownCapture={handleCropInteraction}
      onMouseDownCapture={handleCropInteraction}
      onTouchStartCapture={handleCropInteraction}
      onDoubleClickCapture={handleCropInteraction}
      onMouseLeave={() => setShowPrintSizeMessage(false)}
    >
      <CustomTooltip
        title="Please select the print size."
        open={showPrintSizeMessage && !canAdjustCrop}
        trigger="manual"
        onRequestClose={() => setShowPrintSizeMessage(false)}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />
      <Cropper
        src={photo?.attributes?.presignedPhotoUrl}
        className={styles.cropperStyle}
        sizes="(max-width: 479px) 247.5px, (max-width: 767px) 52vw, (max-width: 1439px) 43vw, 44vw"
        ref={cropperRef}
        guides
        crop={handleCrop}
        ready={handleOnReady}
        viewMode={2}
        dragMode="none"
        minCropBoxHeight={10}
        minCropBoxWidth={10}
        responsive
        autoCropArea={1}
        checkCrossOrigin={false}
        movable={false}
        autoCrop={false}
        zoomable={false}
        cropBoxMovable={true}
        background={false}
        modal={false}
      />
      {showShapeMask && cropBoxRect && (() => {
        const { rx, ry } = getShapeRadii(cropBoxRect, cropShape);
        const maskId = `mat-opening-cutout-${cropShape}`;
        return (
          <svg
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: cropBoxRect.left,
              top: cropBoxRect.top,
              width: cropBoxRect.width,
              height: cropBoxRect.height,
              pointerEvents: 'none',
              overflow: 'visible',
            }}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <mask id={maskId}>
                <rect width="100" height="100" fill="white" />
                <ellipse cx="50" cy="50" rx={rx} ry={ry} fill="black" />
              </mask>
            </defs>
            <rect
              width="100"
              height="100"
              fill="rgba(0, 0, 0, 0.55)"
              mask={`url(#${maskId})`}
            />
          </svg>
        );
      })()}
      <div className="prem-frame-wrap">
        <div className="prem-frame" />
      </div>
    </div>
  );
};

export default PhotoCropper;
